import gsap from 'gsap';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { Container, Graphics, Sprite, Text } from '@pixi/react';
import { BoostedJackpotIconSliderBoardProps } from '../../../index.types';
import { CreateTextWinAmountAndCurrencySymbol } from '../../../createTextWinAmountAndCurrencySymbol';
import { useTranslation, useStore } from '../../../../../../../hooks';
import { isMobile } from '../../../../../../../helpers';
import { T } from '../../../../../../../constants';

export const BoostedJackpotIconSliderBoard: React.FC<BoostedJackpotIconSliderBoardProps> = observer(({
  playerBoostedWinAmount,
  setPlayerBoostedWinAmount,
  boostedJackpotIconInfoBoardRectangle,
}) => {
  const t = useTranslation();
  const { gameStore } = useStore();
  const useMobileLayout = isMobile() || !!gameStore.canvasWidth;
  const containerRef = useRef<any>();
  const containerSliderRef: any = useRef();
  const enterTweenRef = useRef<gsap.core.Tween | null>(null);
  const exitTweenRef = useRef<gsap.core.Tween | null>(null);
  const exitDelayRef = useRef<number | null>(null);
  const [mask, setMask] = useState(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.mask = mask;
    };
  }, [mask, containerRef]);

  useEffect(() => {
    if (containerSliderRef.current) {
      startAnimation()
    };

    return () => {
      enterTweenRef.current?.kill();
      enterTweenRef.current = null;
      exitTweenRef.current?.kill();
      exitTweenRef.current = null;
      if (exitDelayRef.current) {
        window.clearTimeout(exitDelayRef.current);
        exitDelayRef.current = null;
      }
      if (containerSliderRef.current) {
        gsap.killTweensOf(containerSliderRef.current);
      }
    };
  }, []);

  const startAnimation = () => {
    const sliderContainer = containerSliderRef.current;
    if (!sliderContainer) return;

    const targetX = useMobileLayout ? -150 : 300;
    const startX = useMobileLayout ? 0 : 350;

    enterTweenRef.current?.kill();
    exitTweenRef.current?.kill();
    if (exitDelayRef.current) {
      window.clearTimeout(exitDelayRef.current);
      exitDelayRef.current = null;
    }
    sliderContainer.x = startX;
    sliderContainer.y = useMobileLayout ? -34 : 0;

    enterTweenRef.current = gsap.to(sliderContainer, {
      x: useMobileLayout ? 20 : 72,
      duration: useMobileLayout ? 1 : 1.5,
      ease: 'power2.out',
      onComplete: () => {
        exitDelayRef.current = window.setTimeout(() => {
          exitTweenRef.current = gsap.to(sliderContainer, {
            x: targetX,
            y: useMobileLayout ? -34 : 0,
            duration: useMobileLayout ? 1 : 1.5,
            ease: 'power2.in',
            onComplete: () => {
              setPlayerBoostedWinAmount(null);
            },
          });
        }, 3000);
      },
    });
  };
  return (
    <Container ref={containerRef} {...({ name: 'BoostedJackpotIconSliderBoard' } as any)}>
      <Container ref={containerSliderRef}>
        <Sprite texture={boostedJackpotIconInfoBoardRectangle} anchor={0.5} />
        <Text
          x={useMobileLayout ? 0 : -14}
          y={useMobileLayout ? -6 : -10}
          anchor={0.5}
          resolution={2}
          {...({ name: 'text' } as any)}
          text={t(T.LAST_PLAYER_HAS_WON)}
          style={{
            fontSize: useMobileLayout ? 9 : 12,
            wordWrap: true,
            align: 'center',
            fill: '#FFC33E',
            fontWeight: '600',
            wordWrapWidth: 206,
            fontFamily: 'poppinsMedium',
          }}
        />
        <CreateTextWinAmountAndCurrencySymbol
          x={useMobileLayout ? 5 : -14}
          y={useMobileLayout ? 6 : 11}
          margin={15}
          fontSize={useMobileLayout ? 12 : 16}
          color='#FFCD6A'
          amount={playerBoostedWinAmount}
        />
      </Container>
      <Graphics
        draw={(g) => {
          g.beginFill(0x000000);
          g.drawRect(useMobileLayout ? -60 : -33, useMobileLayout ? -120 : -70, 200, 100);
        }}
        ref={(graphicsRef: any) => setMask(graphicsRef)}
      />
    </Container>
  );
});
